import {
  CalendarCheck,
  CalendarDots,
  Clock,
  Lock,
  MapPin,
  Money,
  Repeat,
  UsersThree,
  XCircle,
  type Icon,
} from '@phosphor-icons/react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { localizedCategoryName } from '@/lib/category-labels'
import { formatCurrency, formatDateTime } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { kuniriIconWeight } from '@/lib/icons'

import type { ActivityDetail } from '../api'
import { activityICS } from '../links'
import { addToCalendar } from '../calendar'
import { recurrenceLabel } from '@/features/activities/recurrence'
import { Callout } from './callout'

interface ActivityOverviewProps {
  activity: ActivityDetail
  canViewSensitive: boolean
}


function modeKey(mode: ActivityDetail['participationMode']): string {
  if (mode === 'invite') return 'activityDetail.mode.invite'
  if (mode === 'approval') return 'activityDetail.mode.approval'
  return 'activityDetail.mode.open'
}

interface InfoRowProps {
  icon: Icon
  label: string
  value: string
  detail?: string
}

function InfoRow({ icon: InfoIcon, label, value, detail }: InfoRowProps) {
  return (
    <div className="flex min-w-0 items-start gap-3 p-4">
      <InfoIcon
        size={20}
        weight={kuniriIconWeight}
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-teal"
      />
      <dl className="min-w-0">
        <dt className="text-xs font-bold text-muted">{label}</dt>
        <dd className="mt-0.5 text-[15px] leading-snug font-bold break-words text-deep">
          {value}
        </dd>
        {detail ? (
          <dd className="mt-1 text-xs font-semibold text-muted">{detail}</dd>
        ) : null}
      </dl>
    </div>
  )
}

/**
 * Read-only overview: title, category/mode pills, start/end/spots, privacy
 * callouts, meeting point, cost and description. Cost, description and end
 * time are public (D-081/D-088/D-112); meeting point and location stay hidden
 * unless the viewer is the creator or a confirmed participant (D-043).
 */
export function ActivityOverview({
  activity,
  canViewSensitive,
}: ActivityOverviewProps) {
  const { t } = useTranslation()
  const locale = currentLocale()
  const descriptionId = useId()

  const start = activity.startAt
    ? formatDateTime(activity.startAt, locale)
    : t('activityDetail.info.unknown')
  const end = activity.endAt
    ? formatDateTime(activity.endAt, locale)
    : t('activityDetail.info.unknown')
  const endLabel = t('activityDetail.info.end')
  const calendarAvailable = activityICS(activity) !== null
  const recurrence = activity.isRecurring
    ? recurrenceLabel(activity.recurrenceWeekday, t)
    : null
  // The backend already nulls an empty point; a blank string would still
  // collapse the callout body through `??`, so normalise it here (D-100
  // maintainer report: 'Punto di ritrovo shows only the title').
  const rawPoint = activity.meetingPoint?.trim() || null
  const city = activity.city?.trim() || null
  const locationName =
    activity.locationName?.trim() &&
    activity.locationName.trim() !== activity.city?.trim()
      ? activity.locationName.trim()
      : null
  // The maintainer asked for "Via Emilia 10 · Pisa": the point (street names
  // stay in their original language, nothing is translated) plus the city.
  const withCity = (value: string | null): string | null =>
    value && city && !value.toLowerCase().includes(city.toLowerCase())
      ? `${value} · ${city}`
      : value
  const meetingPoint = withCity(rawPoint)
  const venueFallback = withCity(locationName)

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-[clamp(1.85rem,5vw,2.625rem)] leading-[1.0] font-extrabold tracking-[-0.02em] text-deep">
          {activity.title}
        </h1>
        <div className="mt-3 flex flex-wrap gap-2">
          <span
            className="inline-flex items-center rounded-pill px-[11px] py-[7px] text-[12px] font-bold"
            style={{
              backgroundColor: `color-mix(in srgb, ${activity.categoryColor} 25%, var(--paper))`,
              color: 'var(--deep)',
            }}
          >
            {localizedCategoryName(t, activity.categorySlug, activity.categoryName)}
          </span>
          <span className="inline-flex items-center rounded-pill bg-mint px-[11px] py-[7px] text-[12px] font-bold text-teal">
            {t(modeKey(activity.participationMode))}
          </span>
        </div>
      </div>

      {activity.status === 'cancelled' ? (
        <Callout
          icon={XCircle}
          title={t('activityDetail.status.cancelledTitle')}
          tone="salmon"
        >
          {t('activityDetail.status.cancelledBody')}
        </Callout>
      ) : null}

      <div className="divide-y divide-line rounded-lg border border-line bg-white min-[640px]:grid min-[640px]:grid-cols-3 min-[640px]:divide-x min-[640px]:divide-y-0">
        <InfoRow
          icon={CalendarDots}
          label={t('activityDetail.info.start')}
          value={start}
        />
        <InfoRow icon={Clock} label={endLabel} value={end} />
        <InfoRow
          icon={UsersThree}
          label={t('activityDetail.info.spots')}
          value={`${activity.currentParticipantsCount}/${activity.maxParticipants}`}
          detail={t('activityDetail.availableSpots', {
            count: activity.availableSpots,
          })}
        />
      </div>

      {recurrence ? (
        <Callout
          icon={Repeat}
          title={recurrence}
          tone="mint"
        >
          {t('activityDetail.recurrence.body')}
        </Callout>
      ) : null}

      {calendarAvailable ? (
        <button
          type="button"
          className="btn-secondary w-full bg-white text-base min-[640px]:w-auto"
          onClick={() => void addToCalendar(activity)}
        >
          <CalendarCheck
            size={20}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {t('activityDetail.calendarButton')}
        </button>
      ) : null}

      {!canViewSensitive ? (
        <Callout
          icon={Lock}
          title={t('activityDetail.privacy.prejoinTitle')}
          tone="mint"
        >
          {t('activityDetail.privacy.prejoinBody')}
        </Callout>
      ) : null}

      {canViewSensitive && (meetingPoint || venueFallback) ? (
        <Callout icon={MapPin} title={t('activityDetail.meetingPoint.title')}>
          <p className="font-semibold text-deep">
            {meetingPoint ?? venueFallback}
          </p>
          {meetingPoint && venueFallback ? (
            <p className="mt-0.5">{venueFallback}</p>
          ) : null}
        </Callout>
      ) : null}

      {/* Cost is public: visible to every viewer, participants or not (D-081). */}
      <Callout icon={Money} title={t('activityDetail.price.title')}>
        <p className="font-semibold text-deep">
          {activity.priceType === 'free'
            ? t('activityDetail.price.free')
            : activity.estimatedPrice != null
              ? t('activityDetail.price.estimate', {
                  amount: formatCurrency(activity.estimatedPrice, locale),
                })
              : t('activityDetail.price.estimateFallback')}
        </p>
        {activity.priceType !== 'free' && activity.priceDescription ? (
          <p className="mt-0.5">{activity.priceDescription}</p>
        ) : null}
      </Callout>

      <section aria-labelledby={descriptionId}>
        <h2
          id={descriptionId}
          className="font-head text-[18px] font-extrabold text-deep"
        >
          {t('activityDetail.description.title')}
        </h2>
        {activity.description.trim() ? (
          <p className="mt-2 max-w-[72ch] text-[15px] leading-relaxed break-words whitespace-pre-line text-deep">
            {activity.description}
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted">
            {t('activityDetail.description.empty')}
          </p>
        )}
      </section>

    </div>
  )
}
